import { T, LogoMark, Wordmark } from '../theme';
import GlobalFooterBar from './GlobalFooterBar';

const EFFECTIVE_DATE = 'June 9, 2026';

function TermsPage({ onClose, embedded = false, standalone = false }) {
  const wrap = standalone ? {
    minHeight: '100vh',
    background: T.appBg,
    fontFamily: T.fontBody,
    paddingBottom: 36,
  } : {
    position: embedded ? 'relative' : 'fixed',
    inset: embedded ? undefined : 0,
    background: embedded ? 'transparent' : 'rgba(0,0,0,0.6)',
    zIndex: embedded ? undefined : 2000,
    display: 'flex',
    alignItems: embedded ? undefined : 'center',
    justifyContent: embedded ? undefined : 'center',
    backdropFilter: embedded ? undefined : 'blur(4px)',
  };

  const card = {
    background: '#fff',
    borderRadius: standalone ? 0 : (embedded ? 0 : 20),
    width: '100%',
    maxWidth: (embedded || standalone) ? undefined : 640,
    maxHeight: embedded ? undefined : (standalone ? undefined : '88vh'),
    overflowY: (embedded || standalone) ? undefined : 'auto',
    padding: standalone ? '32px 24px' : '32px 28px',
    boxSizing: 'border-box',
    fontFamily: T.fontBody,
  };

  const h2 = { fontSize: 15, fontWeight: 800, color: T.navy, margin: '24px 0 8px' };
  const p  = { fontSize: 14, color: '#374151', lineHeight: 1.7, margin: '0 0 10px' };
  const li = { fontSize: 14, color: '#374151', lineHeight: 1.7, marginBottom: 6 };

  const inner = (
    <div style={card}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <LogoMark size={22} />
              <Wordmark size={16} lightColor='#080808' />
            </div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: T.navy, margin: 0 }}>Terms of Service</h1>
            <p style={{ ...p, margin: '4px 0 0', color: '#6B7280', fontSize: 13 }}>Effective {EFFECTIVE_DATE}</p>
          </div>
          {onClose && (
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#9CA3AF', padding: 4 }}>×</button>
          )}
        </div>

        <p style={p}>
          Welcome to MySpend.info. By accessing or using our service you agree to be bound by these Terms of Service ("Terms"). Please read them carefully. If you do not agree, do not use MySpend.info.
        </p>

        <h2 style={h2}>1. The Service</h2>
        <p style={p}>
          MySpend.info is a personal and business financial tracking application. Core capabilities include:
        </p>
        <ul style={{ paddingLeft: 20, margin: '0 0 10px' }}>
          <li style={li}><strong>Expense reports</strong> — record, categorize, scan, and export receipts and expenses with multi-currency support.</li>
          <li style={li}><strong>Transaction accounts</strong> — import and manage bank, credit card, brokerage, and loan statements via CSV, XLSX, or direct bank connectivity (Plaid). Transactions are categorized, classified, and tracked for business vs. personal use.</li>
          <li style={li}><strong>AI receipt scanning</strong> — photograph or upload a receipt and AI extracts the vendor, amount, date, and category automatically.</li>
          <li style={li}><strong>AI Bookkeeper</strong> — an optional AI assistant that can read, classify, and annotate your financial data through a secure API connection.</li>
          <li style={li}><strong>Reimbursement tracking</strong> — log amounts owed to you by clients or employers, mark items as reimbursed, and monitor outstanding balances.</li>
          <li style={li}><strong>Sharing &amp; collaboration</strong> — share expense reports with other MySpend users or generate password-protected review links for external parties (e.g. accountants, clients).</li>
        </ul>
        <p style={p}>
          A free tier is available indefinitely with no credit card required. Optional paid plans unlock higher usage limits and advanced features including bank connectivity and AI Bookkeeper access.
        </p>

        <h2 style={h2}>2. Accounts</h2>
        <p style={p}>
          You may use MySpend.info as a guest (data stored locally in your browser) or create an account to sync data across devices. You are responsible for maintaining the confidentiality of your credentials and for all activity under your account. You must provide accurate information at registration and keep it up to date. You must be at least 13 years old to create an account.
        </p>

        <h2 style={h2}>3. Data &amp; Privacy</h2>
        <p style={p}>
          We store your financial data to provide the service. Authenticated user data is stored in Google Firebase (Firestore and Storage), subject to Google's data processing terms. Receipt images uploaded for AI scanning are sent to an AI model provider for processing. You can export or delete your data at any time from within the app. For full details on what we collect and how we use it, see our <a href="/privacy" style={{ color: T.tealDeep }}>Privacy Policy</a>.
        </p>

        <h2 style={h2}>4. AI Features</h2>
        <p style={p}><strong>Receipt scanning:</strong> The receipt scanning feature uses AI to extract data from images. Results are best-effort and may contain errors. You are responsible for reviewing and correcting extracted data before relying on it for financial, tax, or legal purposes. MySpend.info is not liable for inaccuracies produced by AI scanning.</p>
        <p style={p}><strong>AI Bookkeeper:</strong> The AI Bookkeeper feature uses AI models to analyze, interpret, summarize, extrapolate and otherwise understand data you provided to MySpend.info. This may be combined with other sources to fully understand scenarios. By using MySpend.info, you acknowledge and understand that AI may provide information or advice that is factually incorrect or misleading. You are responsible for verifying any advice provided by the AI Bookkeeper before taking any action based on it. MySpend.info is not liable for any damages or losses resulting from reliance on AI-generated advice or information. The AI Bookkeeper can read and modify your data (e.g. classify transactions, update categories, mark reimbursements). You are responsible for reviewing any changes it makes. Usage of the AI Bookkeeper is metered and subject to plan limits.</p>
        <p style={p}><strong>Third-party AI connections via MCP:</strong> MySpend.info exposes a Model Context Protocol (MCP) server that allows you to connect your MySpend.info account to third-party AI applications and services of your choosing — for example, AI assistants, automation tools, or other platforms that support the MCP standard. When you establish such a connection, you are granting that third-party service access to your MySpend.info data, which may include expense records, transaction history, account balances, receipt images, and other financial information stored in your account.</p>
        <p style={p}>You are solely responsible for evaluating the trustworthiness and terms of any third-party service you connect to via MCP. MySpend.info does not control, endorse, audit, or take responsibility for the conduct, data practices, security, or advice of any third-party service. Once your data is transmitted to a third-party service, that service's own privacy policy and terms govern how it is handled. MySpend.info is not liable for any unauthorized access, data breach, misuse of your financial data, inaccurate advice, or any other harm arising from your use of a third-party MCP connection. You may revoke access to connected services at any time by disconnecting them from within the app or from the third-party service.</p>
        <h2 style={h2}>5. Bank Connectivity (Plaid)</h2>
        <p style={p}>
          MySpend.info offers optional bank account connectivity powered by Plaid. When you connect a financial institution, you are also agreeing to <a href="https://plaid.com/legal/end-user-privacy-policy/" style={{ color: T.tealDeep }}>Plaid's End User Privacy Policy</a>. MySpend.info offers two storage modes for your Plaid access credentials:
        </p>
        <ul style={{ paddingLeft: 20, margin: '0 0 10px' }}>
          <li style={li}><strong>Local mode:</strong> Your Plaid access token is encrypted and stored only in your browser. Every sync proxies the token through a MySpend server function in-memory; it is never written to our servers.</li>
          <li style={li}><strong>Cloud mode (Pro):</strong> Your Plaid access token is encrypted and stored in Firestore under your account, enabling background syncing. It is accessible only to your account.</li>
        </ul>
        <p style={p}>
          Connecting a financial institution grants MySpend.info read-only access to your transaction history and account balances for the purpose of importing data into the app. We do not initiate payments or transfers on your behalf.
        </p>

        <h2 style={h2}>6. Sharing &amp; Review Links</h2>
        <p style={p}>
          You may share expense reports with other registered MySpend users ("collaborators") or generate a review link to share a read-only view with anyone who has the link. Review links may be password-protected. You are responsible for deciding who you share your financial data with. MySpend.info is not liable for unauthorized disclosure resulting from links you share. You can revoke any review link at any time from within the app.
        </p>
        <p style={p}>
          Reviewers who access your data via a review link may leave comments. You retain ownership of your data; reviewers do not gain the ability to modify your expense records.
        </p>

        <h2 style={h2}>7. Reimbursement Tracking</h2>
        <p style={p}>
          The reimbursement tracking feature allows you to log amounts owed to you and mark them as received. This feature is informational only. MySpend.info does not process, guarantee, or facilitate any actual payment or reimbursement. Reimbursement records are based solely on information you enter and do not constitute a legal record of payment received.
        </p>

        <h2 style={h2}>8. Paid Plans &amp; Billing</h2>
        <p style={p}>
          Paid plans are billed monthly via Stripe. You may cancel at any time; your plan remains active until the end of the current billing period. We reserve the right to change pricing with 30 days' notice. Refunds are provided at our discretion. If payment fails, your account will revert to the Free plan and your data will be preserved. Feature access (including AI Bookkeeper calls and bank connections) is subject to the limits of your current plan.
        </p>

        <h2 style={h2}>9. Acceptable Use</h2>
        <p style={p}>
          You agree not to: (a) use the service for illegal purposes; (b) attempt to reverse-engineer, scrape, or interfere with the service; (c) submit malicious files or content; (d) impersonate others; (e) exceed usage limits through automated means; (f) use the AI Bookkeeper to generate misleading financial records; or (g) share review links in a way that violates applicable privacy laws.
        </p>
        <p style={p}>
          We reserve the right to suspend or terminate accounts that violate these terms.
        </p>

        <h2 style={h2}>10. Intellectual Property</h2>
        <p style={p}>
          You retain ownership of the financial data you create and import. You grant MySpend.info a limited license to store, process, and transmit that data solely to provide the service. The MySpend.info name, logo, and application code are proprietary and may not be reproduced without permission.
        </p>

        <h2 style={h2}>11. Disclaimer &amp; Limitation of Liability</h2>
        <p style={p}>
          MySpend.info is provided "as is" without warranties of any kind. We are not a financial advisor, tax advisor, or accounting service. Nothing in the app constitutes financial, tax, or legal advice. You are solely responsible for the accuracy of your records and for any decisions made based on data in the app. We are not liable for data loss, inaccurate records, tax filing errors, reimbursement disputes, bank connectivity failures, or any indirect or consequential damages arising from use of the service. Our total liability to you shall not exceed the amount you paid us in the 12 months prior to the claim.
        </p>

        <h2 style={h2}>12. Changes to These Terms</h2>
        <p style={p}>
          We may update these Terms from time to time. We will notify users of material changes via email or an in-app notice. Continued use of MySpend.info after changes take effect constitutes acceptance of the updated Terms.
        </p>

        <h2 style={h2}>13. Contact</h2>
        <p style={p}>
          Questions about these Terms? Contact us at <span style={{ color: T.tealDeep, fontWeight: 600 }}>support@myspend.info</span>.
        </p>

        {onClose && (
          <button
            onClick={onClose}
            style={{
              marginTop: 24, width: '100%', padding: '13px', borderRadius: 12, border: 'none',
              background: T.gradTeal, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Close
          </button>
        )}
      </div>
  );

  if (standalone) {
    return (
      <div style={wrap}>
        <div style={{ background: T.gradNavy, padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <LogoMark size={20} />
            <Wordmark size={14} />
          </div>
          <a href="/" style={{ color: '#fff', fontSize: 13, fontWeight: 600, textDecoration: 'none', opacity: 0.85 }}>← Open App</a>
        </div>
        <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 0 84px' }}>
          {inner}
        </div>
        <GlobalFooterBar />
      </div>
    );
  }

  return (
    <div style={wrap} onClick={!embedded ? (e => { if (e.target === e.currentTarget) onClose?.(); }) : undefined}>
      {inner}
    </div>
  );
}

export default TermsPage;
